import { useCallback, useEffect, useState } from 'react';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { RbiOrderStatus } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { useIsOrderInProgress } from 'hooks/use-is-order-in-progress';
import { useReorderableOrders } from 'hooks/use-reorderable-orders';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { useStoreContext } from 'state/store';
import { getSelectionsListFromEntry } from 'utils/cart';

import { MappedCartEntry, MappedOrder } from './types';

const hasSufficientAvailability = (orderEntries: MappedCartEntry[]): boolean => {
  // @TODO: Check what happens if cart extra is unavailable
  const unavailableCartEntries = orderEntries.filter(
    entry =>
      entry.status !== ItemAvailabilityStatus.AVAILABLE &&
      entry.status !== ItemAvailabilityStatus.CART_EXTRA
  ).length;
  const totalItemsInCart = orderEntries.length || 0;
  return totalItemsInCart / 2 >= unavailableCartEntries;
};

export const useLastOrderAvailableToReorder = (): MappedOrder | undefined => {
  // this is needed to avoid calling mapServerEntriesToFrontend from effect if the component is unmounted
  const isMounted = useIsComponentMounted();
  const { cartEntries } = useCart();
  const mapToFrontendEntry = useMapToFrontendCartEntry();
  const isOrderInProgress = useIsOrderInProgress();
  const { noStoreSelected, isStoreOpenAndAvailable } = useStoreContext();
  const [lastOrderCartEntries, setLastOrderCartEntries] = useState<MappedCartEntry[]>();

  const { reorderableOrders } = useReorderableOrders(
    {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.PAYMENT_SUCCESSFUL],
    },
    'no-cache'
  );

  const lastOrder = reorderableOrders?.[0];

  const mapServerEntriesToFrontend = useCallback(
    async (lastOrderCart: IHistoricalOrder['cart']): Promise<void> => {
      if (!lastOrderCart.reorderCartEntries?.length) {
        return;
      }

      const orderCartEntries: MappedCartEntry[] = [];

      for (const backendCartEntry of lastOrderCart.reorderCartEntries) {
        const { status, cartEntry } = await mapToFrontendEntry(backendCartEntry, cartEntries);
        const itemSelections = cartEntry?.children ? getSelectionsListFromEntry(cartEntry) : [];
        orderCartEntries.push({
          ...cartEntry,
          itemSelections,
          status,
          // @TODO: Check what happens if cart extra is unavailable
          isAvailable: [
            ItemAvailabilityStatus.AVAILABLE,
            ItemAvailabilityStatus.CART_EXTRA,
          ].includes(status),
        });
      }

      if (!isMounted.current) {
        return;
      }

      setLastOrderCartEntries(orderCartEntries);
    },
    [cartEntries, isMounted, mapToFrontendEntry]
  );

  useEffect(() => {
    if (lastOrder) {
      mapServerEntriesToFrontend(lastOrder.cart);
    }
  }, [mapServerEntriesToFrontend, lastOrder]);

  if (noStoreSelected || !isStoreOpenAndAvailable || isOrderInProgress || !lastOrder) {
    return;
  }

  const orderIsValid =
    lastOrder && lastOrderCartEntries && hasSufficientAvailability(lastOrderCartEntries);

  return orderIsValid ? { ...lastOrder, orderEntries: lastOrderCartEntries } : undefined;
};
